import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Names, Values } from '@mode-switch/model';
import {
  ColorTarget,
  EncColor,
  EncField,
  EncSeries,
  Encoding,
  EncodingResult,
  MarkType,
  MarksSummary,
  VariableType,
} from '@mode-vegas/vegas';
import { AccountFacade } from '@mode/accounts/contract';
import { IconComponent } from '@mode/capra';
import { ColorsModalData, ColorsModalFacade } from '@mode/charts/contract';
import { SelectedChartService } from '@mode/charts/contract';
import {
  EncodingHelpers,
  FieldFormatHelpers,
  RenderDataModel,
  buildDefaultContinuousFieldColor,
  computePalette,
  resolveFieldColor,
} from '@mode/charts/util-vegas';
import { BinSizeTypes, ChartTypes, FlamingoTypes, OrgTypes } from '@mode/shared/contract-common';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { Subject, takeUntil } from 'rxjs';
import { ColorSwatchFlyoutComponent } from '../../colors/color-swatch-flyout/color-swatch-flyout.component';
import * as MarksHelpers from '../marks-helpers';
import { ChannelComponent, EncodingUpdateType } from './channel.component';

@Component({
  standalone: true,
  imports: [ChannelComponent, ColorSwatchFlyoutComponent, CommonModule, IconComponent],
  selector: 'mode-color-channel-component',
  templateUrl: './color-channel.component.html',
  styleUrls: ['./color-channel.component.scss'],
})
export class ColorChannelComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) channelSummary!: MarksHelpers.ChannelSummary;
  @Input({ required: true }) encoding!: Encoding;
  @Input({ required: true }) model!: FlamingoTypes.ModeModel;
  @Input() renderModel: RenderDataModel | undefined;
  @Input({ required: true }) view!: FlamingoTypes.ModeView;
  @Input({ required: true }) chartService!: SelectedChartService;
  @Input({ required: true }) chartType!: ChartTypes.ChartType;
  @Input() series: EncSeries | false = false;
  @Input({ required: true }) format!: ChartTypes.ChartFormat;
  @Input() queryToken: string | undefined;
  @Input() queryRunToken: string | undefined;
  @Input() vizToken: string | undefined;
  @Input() allowScalars = true;
  @Input() allowAggregates = true;
  @Input() allowDiscrete = true;
  @Input() allowContinuous = true;
  @Input() fieldFormatMap: ChartTypes.FieldFormatMap | undefined;
  @Input() fieldColorMap: ChartTypes.FieldColorMap | undefined;
  @Input() binSizeToFieldMap: BinSizeTypes.BinSizeToFieldMap | undefined;
  @Input() isAutoOrManualRender = true;
  @Input() currentColorPaletteToken: string | undefined;
  @Input() disabled = false;
  @Input() useMeasureNamesPill = false;
  @Input() zone: EncodingHelpers.ZoneType | undefined;
  @Input() hideAdvancedColorSelection = false;

  activeFieldColor: string | undefined;
  allColorPalettes: OrgTypes.ColorPalette[] = [];
  selectedColorPalette: OrgTypes.ColorPalette | undefined;
  canUseAsBackground = false;
  isUseAsBackground = false;
  showTextSettings = false;
  summary: MarksSummary | undefined;
  readonly baseColorIcon = MarksHelpers.IconChannelMap['color'];
  icon = this.baseColorIcon;
  showFlyout = false;
  hasSplitLegend = false;

  private destroyed$ = new Subject<void>();

  constructor(
    private accountFacade: AccountFacade,
    private colorsModalFacade: ColorsModalFacade,
    private analyticsService: AnalyticsService
  ) {
    this.accountFacade.reportOwnerColorPalettes$.pipe(takeUntil(this.destroyed$)).subscribe((palettes) => {
      this.allColorPalettes = [...palettes].sort((a, b) => a.name.localeCompare(b.name));
      const currentPaletteToken = this.getSelectedColorPaletteToken();
      this.selectedColorPalette = this.allColorPalettes.find((palette) => palette.token === currentPaletteToken);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.activeFieldColor = this.determineActiveFieldColor();

    this.summary = EncodingHelpers.getSummary(this.encoding);
    this.canUseAsBackground = this.summary.type === MarkType.Text && this.encoding.marks.channel === '*';
    this.isUseAsBackground = this.canUseAsBackground && this.encoding.marks.properties.color.target === 'background';

    this.showTextSettings = this.determineShowTextSettings();

    this.icon = `${this.baseColorIcon}${this.disabled ? '-disabled' : ''}`;

    this.showFlyout = this.channelSummary.field == null && this.channelSummary.type !== 'multiple';

    const measureValuesKey = FieldFormatHelpers.getFormatMapKey(Values.Key, VariableType.CONTINUOUS);
    this.hasSplitLegend = this.fieldFormatMap?.[measureValuesKey]?.legendFormat?.splitLegend ?? false;

    const currentPaletteToken = this.getSelectedColorPaletteToken();
    this.selectedColorPalette = this.allColorPalettes.find((palette) => palette.token === currentPaletteToken);
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  handleEncodingUpdate(update: {
    change: EncodingUpdateType;
    encodingResult: EncodingResult;
    fieldFormatMap: ChartTypes.FieldFormatMap;
  }) {
    switch (update.change) {
      case 'dropOnEmpty':
      case 'dropOnField':
      case 'fieldUpdate':
        this.updateChartWithDefaultColor(update.encodingResult, update.fieldFormatMap);
        break;
      default: {
        this.chartService.dispatchEncodingAndFormatUpdate(update.encodingResult, undefined, update.fieldFormatMap);
        break;
      }
    }
  }

  updateChartWithDefaultColor(result: EncodingResult, fieldFormatMap: ChartTypes.FieldFormatMap) {
    const colorField = this.getColorField(result.encoding);
    if (colorField?.variable.type === VariableType.CONTINUOUS) {
      const defaultFieldColor = buildDefaultContinuousFieldColor(
        result.encoding,
        this.renderModel,
        this.series ? this.series : undefined,
        this.fieldColorMap,
        this.allColorPalettes
      );
      const fieldColors = { ...this.fieldColorMap, ...(defaultFieldColor ?? {}) };
      this.chartService.dispatchEncodingAndFormatUpdate(
        result,
        undefined,
        { ...this.fieldFormatMap, ...fieldFormatMap },
        undefined,
        fieldColors
      );
    } else {
      this.chartService.dispatchEncodingAndFormatUpdate(result, undefined, {
        ...this.fieldFormatMap,
        ...fieldFormatMap,
      });
    }
  }

  determineActiveFieldColor() {
    let color: string | undefined;
    if (this.series) {
      color = this.series.properties.color.value;
    } else if (this.encoding.marks.channel === '*') {
      color = this.encoding.marks.properties.color.value;
    } else if (this.encoding.marks.series.length > 0) {
      color = this.encoding.marks.series[0].properties.color.value;
    }
    if (color == null) {
      // fallback to org default palette
      color = this.format?.colors?.custom[0];
    }
    return color;
  }

  getColorField(encoding: Encoding) {
    let colorField: EncField | undefined;
    if (encoding.marks.channel === '*') {
      colorField = encoding.marks.properties.color.values[0];
    } else {
      colorField = this.series
        ? this.series.properties.color.values[0]
        : encoding.marks.series[0]?.properties.color.values[0];
    }
    return colorField;
  }

  getSelectedColorPaletteToken(): string {
    if (this.series) {
      return this.getColorPaletteFromColorProp(this.series.properties.color);
    } else if (this.encoding && this.encoding.marks.series.length) {
      return this.getColorPaletteFromColorProp(this.encoding.marks.series[0].properties.color);
    } else if (this.encoding) {
      return this.getColorPaletteFromColorProp(this.encoding.marks.properties.color);
    }

    return '';
  }

  getColorPaletteFromColorProp(color: EncColor): string {
    if (color.values?.length) {
      const colorCompiledField = this.renderModel?.compileEncField(color.values[0]);
      const fieldColor = resolveFieldColor(colorCompiledField, this.fieldColorMap);
      if (fieldColor) {
        return fieldColor.colorPaletteToken;
      }
    }
    return this.currentColorPaletteToken || '';
  }

  determineShowTextSettings(): boolean {
    if (!this.series) {
      return this.summary?.type === MarkType.Text;
    }

    return this.series.properties.type?.value === MarkType.Text;
  }

  onColorSwatchFlyoutUpdated(colorSelected: string, selectedPalette: OrgTypes.ColorPalette, series: EncSeries | false) {
    const seriesId = series ? series.field : undefined;
    const newEncoding = EncodingHelpers.updateMarkColor(this.encoding, colorSelected, seriesId);
    this.selectedColorPalette = selectedPalette;
    this.chartService.dispatchEncodingAndFormatUpdate(
      newEncoding,
      undefined,
      undefined,
      undefined,
      undefined,
      selectedPalette.token
    );
  }

  onMarkColorTargetUpdate(target: ColorTarget) {
    if (target === 'background') {
      this.setColorAsBackground();
    }

    if (target === 'foreground') {
      this.setColorAsText();
    }
  }

  setColorAsBackground(): void {
    this.chartService.dispatchEncodingUpdate(EncodingHelpers.updateMarkColorTarget(this.encoding, 'background'));
    this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
      location: AnalyticsEvent.SubContext.Chart,
      action: 'Apply color to background',
      resource: AnalyticsEvent.Context.Visualization,
      chartType: this.chartType,
    });
  }

  setColorAsText(): void {
    this.chartService.dispatchEncodingUpdate(EncodingHelpers.updateMarkColorTarget(this.encoding, 'foreground'));
    this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
      location: AnalyticsEvent.SubContext.Chart,
      action: 'Apply text color to foreground',
      resource: AnalyticsEvent.Context.Visualization,
      chartType: this.chartType,
    });
  }

  openColorModal() {
    const colorField =
      this.channelSummary.field ?? EncodingHelpers.getEncFieldById(this.encoding, this.channelSummary.id ?? '');

    if (colorField != null && this.colorsModalFacade && this.view) {
      const data: ColorsModalData = {
        palettes: this.allColorPalettes || [],
        currentPaletteToken: this.getSelectedColorPaletteToken(),
        formatDefaultColor: computePalette(this.format)[0],
        queryRunToken: this.queryRunToken,
        vizToken: this.vizToken ?? '',
        view: this.view,
        encoding: this.encoding,
        colorField: colorField,
        fieldColors: this.fieldColorMap,
        renderModel: this.renderModel,
        fieldFormats: this.fieldFormatMap,
        chartHasSplitLegend: this.hasSplitLegend && EncodingHelpers.isInEncoding(this.encoding, Names.Key),
        isManualUpdateMode: !this.isAutoOrManualRender,
        savedColorTarget: this.encoding.marks.properties.color.target,
        applyColors: this.onColorFieldUpdate.bind(this),
        onCancel: () => {},
      };

      this.colorsModalFacade.open(data);
    }
  }

  onColorFieldUpdate(field: EncField, color: ChartTypes.FieldColor, colorTarget: ColorTarget) {
    this.chartService.updateColorField(
      field.formula,
      field.variable.type,
      color.colorPaletteToken,
      [...color.palette],
      undefined,
      color.map,
      color.gradient,
      color.unassignedColor,
      colorTarget
    );
  }
}
